import React, { useState, useEffect, useContext } from "react";
import ReactEcharts from "echarts-for-react";
import { SizeContext } from "layouts/AppMain";
import { addNumUnit } from "utils/index";

type DataType = {
  name: string;
  value: number;
};

interface RingBarProps {
  data: DataType[];
  color?: string[];
}

function getTop5(data: DataType[]) {
  const top5 = data
    .filter(({ name }) => !/其他|其它|other|QR-Code|N\/A/.test(name))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);

  const other = data
    .filter(({ name }) => !top5.find((t) => t.name === name))
    .reduce((a, b) => a + b.value, 0);

  return top5
    .concat(
      other === 0
        ? []
        : [
            {
              name: "Others",
              value: other,
            },
          ]
    )
    .sort((a, b) => b.value - a.value);
}

const getOption = (
  oData: DataType[] = [],
  color: string[] = [],
  s: (n: number, b?: boolean) => number
) => {
  const data = getTop5(oData);

  const sumValue = data.map((d) => d.value).reduce((a, b) => a + b, 0);
  const objData = Object.fromEntries(
    data.map(({ name, value }) => [name, value])
  );

  const option = {
    color,
    legend: {
      show: true,
      top: "center",
      left: "60%",
      padding: [0, 0],
      itemGap: s(12),
      itemWidth: s(12),
      itemHeight: s(12),
      data: data.map((d) => d.name),
      formatter: (name: string) =>
        `{name|${name[0].toUpperCase()}${name.slice(1)}}{value|${String(
          addNumUnit(objData[name], 1)
        ).replace(/[^.\d]+/, (a) => `}{unit|${a}`)}}`,
      textStyle: {
        rich: {
          name: {
            color: "#b7cfee",
            fontSize: s(16),
            fontFamily: "FZLanTingHei",
            padding: [0, s(8), 0, 0],
          },
          value: {
            color: "#fff",
            fontSize: s(24),
            fontFamily: "DIN",
          },
          unit: {
            color: "#fff",
            fontSize: s(20),
            fontWeight: "900",
          },
        },
      },
    },
    series: data
      .map((d, i) => [
        {
          name: "bar",
          type: "pie",
          hoverAnimation: false,
          radius: [70 - i * 15 + "%", 76 - i * 15 + "%"],
          center: ["30%", "50%"],
          label: {
            show: false,
          },
          itemStyle: {
            label: {
              show: false,
            },
            labelLine: {
              show: false,
            },
            borderWidth: 5,
          },
          data: [
            {
              value: data[i].value,
              name: data[i].name,
            },
            {
              // value: (sumValue * 4) / 3 - data[i].value,
              value: (sumValue * 4) / 4 - data[i].value,
              name: "",
              itemStyle: {
                color: "transparent",
              },
            },
          ],
        },
        {
          name: "bg",
          type: "pie",
          z: 1,
          radius: [70 - i * 15 + "%", 76 - i * 15 + "%"],
          center: ["30%", "50%"],
          label: {
            show: false,
          },
          itemStyle: {
            label: {
              show: false,
            },
            labelLine: {
              show: false,
            },
            borderWidth: 5,
          },
          silent: true,
          data: [
            {
              // value: 7.5,
              value: 10,
              itemStyle: {
                color: "#262677",
              },
            },
            {
              // value: 2.5,
              value: 0,
              name: "",
              itemStyle: {
                color: "transparent",
              },
            },
          ],
        },
      ])
      .reduce((a, b) => a.concat(b), []),
  };

  return option;
};

const RingBar: React.FC<RingBarProps> = ({ data, color }) => {
  const getSize = useContext<any>(SizeContext);
  const [option, setOption] = useState<any>({});

  useEffect(() => {
    setOption(getOption(data, color, getSize));
  }, [color, data, getSize]);

  return (
    <ReactEcharts
      option={option}
      notMerge={true}
      style={{ width: "100%", height: "100%", position: "absolute" }}
    />
  );
};

export default RingBar;
